// FILE: src/components/studyplan/StudyPlanView.js
//
// Study Plan Command Center (redesign, migration 069).
// Layout: CommandHero → CertTrackSwitcher → CertRoadmap (full width) →
// grid [ WeeklyPlan | rail: PrepCoverage + LogisticsChecklist ].
//
// Data:
//   • plan + activities        — loadActiveStudyPlan (study_plans/_activities)
//   • readiness + domain stats — loadSessionsForReadiness + computeReadiness
//   • reading checkoffs        — readingProgressService (learner_prep_tasks)
//   • logistics checklist      — examLogisticsService (learner_prep_tasks)
// All statuses on the roadmap are derived (studyPlanRoadmap.js) — nothing
// is self-reported except the reading/logistics checkoffs themselves.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './StudyPlanViewPage.module.css';
import { getExamConfig, getAllExams, getBaselineColumn } from '../../config/exams';
import { loadActiveStudyPlan } from '../../services/studyPlanService';
import { loadSessionsForReadiness, computeReadiness } from '../../services/readinessService';
import { loadReadingProgress } from '../../services/readingProgressService';
import { loadLogistics } from '../../services/examLogisticsService';
import { deriveRoadmap, currentMilestone, computeDomainStats, deriveNextRisk } from '../../services/studyPlanRoadmap';
import { useLearnerAuth } from '../../contexts/LearnerAuthContext';
import CertTrackSwitcher from '../certification/CertTrackSwitcher';
import LearnerDashboardEmpty from '../dashboard/LearnerDashboardEmpty';
import CommandHero from './CommandHero';
import CertRoadmap from './CertRoadmap';
import WeeklyPlan from './WeeklyPlan';
import PrepCoverage from './PrepCoverage';
import LogisticsChecklist from './LogisticsChecklist';
import { daysUntilDate, formatDateLong, activitiesThisWeek, nextPendingActivity, todayIso } from './studyPlanHelpers';

const ACTIVE_CERTS = getAllExams().filter((e) => !e.comingSoon).map((e) => e.examId);
const EMPTY_READING = { items: [], done: 0, total: 0, pct: 0 };

export default function StudyPlanView({
  isOpen = true,
  onClose,
  activeCert,
  onEditPlan,
  onStartActivity,
  onCertChange,
  onStartCert,
  embedded = false,
  onNavigate,
}) {
  const [state, setState] = useState({
    loading: true, plan: null, activities: [], readiness: null,
    domainStats: [], reading: EMPTY_READING, logistics: [],
  });
  const active = embedded || isOpen;
  const weekRef = useRef(null);

  const { profile } = useLearnerAuth();
  const [baselineDialogOpen, setBaselineDialogOpen] = useState(false);
  const baselines = useMemo(() => {
    const out = {};
    for (const c of ACTIVE_CERTS) out[c] = profile?.[getBaselineColumn(c)] || null;
    return out;
  }, [profile]);

  const reload = useCallback(async (cancelledRef) => {
    const baselineAt = profile?.[getBaselineColumn(activeCert)] || null;
    const [planRes, sessions, reading, logisticsPre] = await Promise.all([
      loadActiveStudyPlan(activeCert),
      loadSessionsForReadiness(activeCert, baselineAt).catch(() => []),
      loadReadingProgress(activeCert),
      loadLogistics(activeCert),
    ]);
    if (cancelledRef && cancelledRef.cancelled) return;
    const readiness = computeReadiness({ examId: activeCert, sessions, baselineAt });
    // Re-run due-date math now that we know the plan's target date.
    const logistics = planRes.plan?.target_exam_date
      ? await loadLogistics(activeCert, { targetExamDate: planRes.plan.target_exam_date })
      : logisticsPre;
    if (cancelledRef && cancelledRef.cancelled) return;
    setState({
      loading: false,
      plan: planRes.plan,
      activities: planRes.activities,
      readiness,
      domainStats: computeDomainStats(sessions, activeCert),
      reading,
      logistics,
    });
  }, [activeCert, profile]);

  useEffect(() => {
    if (!active) return undefined;
    const cancelledRef = { cancelled: false };
    setState((s) => ({ ...s, loading: true, plan: null, activities: [] }));
    reload(cancelledRef);
    return () => { cancelledRef.cancelled = true; };
  }, [active, reload]);

  useEffect(() => {
    if (!active || embedded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, embedded, onClose]);

  // Hooks above any early return (rules-of-hooks).
  const { loading, plan, activities, readiness, domainStats, reading, logistics } = state;
  const daysUntil = plan?.target_exam_date ? daysUntilDate(plan.target_exam_date) : null;
  const thisWeek = useMemo(() => activitiesThisWeek(activities), [activities]);
  const roadmap = useMemo(
    () => deriveRoadmap({ plan, activities, reading, logistics, readiness, daysUntil, examId: activeCert }),
    [plan, activities, reading, logistics, readiness, daysUntil, activeCert],
  );
  const current = useMemo(() => currentMilestone(roadmap), [roadmap]);
  const nextRisk = useMemo(
    () => deriveNextRisk({ logistics, reading, domainStats, activities, daysUntil, examId: activeCert }),
    [logistics, reading, domainStats, activities, daysUntil, activeCert],
  );
  const isLegacyPlan = useMemo(
    () => !!plan && activities.length > 0
      && !activities.some((a) => ['reading', 'logistics', 'exam_day'].includes(a.activity_type)),
    [plan, activities],
  );

  if (!active) return null;

  const cfg = getExamConfig(activeCert);
  const certLabel = cfg?.displayName || (activeCert ? activeCert.toUpperCase() : 'PMP');

  // Legacy portal mode — minimal back-compat shell (unchanged behavior).
  if (!embedded) {
    return createPortal(
      <div
        className={styles.backdrop}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        data-testid="study-plan-view-backdrop"
      >
        <div className={styles.portalPanel} role="dialog" aria-modal="true" aria-labelledby="spv-title">
          <button
            type="button"
            className={styles.closeBtn}
            aria-label="Close study plan"
            onClick={() => onClose?.()}
            data-testid="study-plan-view-close"
          >x</button>
          <h2 id="spv-title">Study plan</h2>
          {loading
            ? <p>Loading...</p>
            : !plan
              ? <p>No active plan yet. Open the embedded page to set one up.</p>
              : <p>Plan loaded. Open the embedded page to manage it.</p>}
        </div>
      </div>,
      document.body,
    );
  }

  function handleContinueToday() {
    const today = todayIso();
    const todays = activities.filter((a) => a.scheduled_for === today && a.status === 'pending');
    const startable = todays.find((a) => ['practice', 'targeted', 'full', 'review'].includes(a.activity_type))
      || (nextPendingActivity(activities) && ['practice', 'targeted', 'full', 'review'].includes(nextPendingActivity(activities).activity_type)
        ? nextPendingActivity(activities) : null);
    if (startable) { onStartActivity?.(startable); return; }
    weekRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleActivityChange(updated, { linkedTask } = {}) {
    setState((s) => ({
      ...s,
      activities: s.activities.map((a) => (a.id === updated.id ? updated : a)),
      // Mirror linked checkoffs into the rail panels without a refetch.
      reading: linkedTask && linkedTask.startsWith('read:')
        ? recountReading(s.reading, linkedTask, updated.status === 'completed')
        : s.reading,
      logistics: linkedTask && !linkedTask.startsWith('read:')
        ? s.logistics.map((t) => (t.key === linkedTask ? { ...t, completed: updated.status === 'completed', overdue: false } : t))
        : s.logistics,
    }));
  }

  function handleReadingChange(refId, checked) {
    setState((s) => ({ ...s, reading: recountReading(s.reading, `read:${refId}`, checked) }));
  }

  function handleLogisticsChange(key, completed) {
    setState((s) => ({
      ...s,
      logistics: s.logistics.map((t) => (t.key === key ? { ...t, completed, overdue: completed ? false : t.overdue } : t)),
    }));
  }

  return (
    <div className={styles.page} data-testid="study-plan-view-page">
      <button
        type="button"
        className={styles.backLink}
        onClick={() => onNavigate?.('dashboard')}
        data-testid="study-plan-view-back"
      >&lsaquo; Back to dashboard</button>

      <CommandHero
        certLabel={certLabel}
        phaseLabel={plan ? current?.label : null}
        readiness={readiness}
        daysUntil={daysUntil}
        examDateLong={plan?.target_exam_date ? formatDateLong(plan.target_exam_date) : null}
        onContinueToday={handleContinueToday}
        canContinue={!loading && !!plan}
        onEditPlan={onEditPlan}
        hasPlan={!!plan}
      />

      <CertTrackSwitcher
        activeCert={activeCert}
        baselines={baselines}
        onSelect={(c) => onCertChange?.(c)}
        onStartCert={() => setBaselineDialogOpen(true)}
        testIdPrefix="plan"
      />

      {loading ? (
        <p className={styles.loading}>Loading your plan...</p>
      ) : !plan ? (
        <EmptyPlan onEditPlan={onEditPlan} certLabel={certLabel} />
      ) : (
        <>
          <CertRoadmap roadmap={roadmap} currentId={current?.id} />
          {isLegacyPlan ? (
            <div className={styles.legacyHint} data-testid="study-plan-view-legacy-hint">
              This plan was generated before reading &amp; logistics tasks existed.
              <button type="button" onClick={() => onEditPlan?.()}>Regenerate your plan</button>
              to get the full roadmap schedule.
            </div>
          ) : null}
          <div className={styles.grid}>
            <div ref={weekRef}>
              <WeeklyPlan
                activities={thisWeek}
                examId={activeCert}
                onStart={onStartActivity}
                onActivityChange={handleActivityChange}
              />
            </div>
            <aside className={styles.rail}>
              <PrepCoverage
                examId={activeCert}
                reading={reading}
                domainStats={domainStats}
                onReadingChange={handleReadingChange}
              />
              <LogisticsChecklist
                examId={activeCert}
                logistics={logistics}
                nextRisk={nextRisk}
                onLogisticsChange={handleLogisticsChange}
              />
            </aside>
          </div>
        </>
      )}

      <LearnerDashboardEmpty
        isOpen={baselineDialogOpen}
        onClose={() => setBaselineDialogOpen(false)}
        onStartCert={onStartCert}
        baselines={baselines}
        displayName={profile?.display_name || null}
      />
    </div>
  );
}

function recountReading(reading, taskKey, checked) {
  const items = reading.items.map((i) => (i.taskKey === taskKey ? { ...i, checked } : i));
  const done = items.filter((i) => i.checked).length;
  return { items, done, total: reading.total, pct: reading.total ? Math.round((100 * done) / reading.total) : 0 };
}

function EmptyPlan({ onEditPlan, certLabel }) {
  return (
    <div className={styles.empty} data-testid="study-plan-view-empty">
      <p className={styles.emptyLead}>
        You haven&rsquo;t set a study plan for {certLabel} yet. Pick a target exam date and we&rsquo;ll lay out the full journey — reading, practice, simulations, and exam logistics.
      </p>
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() => onEditPlan?.()}
        data-testid="study-plan-view-create-cta"
      >Set your exam date &rarr;</button>
    </div>
  );
}
